import { axios } from "../config";
import { EMITTER_ERROR } from "../utils/constants";
import { authEndpoints } from "../utils/constants/httpConstants";
import EventEmitter from "../utils/emitter/EventEmitter";
import {
  INITIAL_ROUTE,
  INVALID_TENANT_ROUTE_PATH,
  LOGIN_PATH,
} from "../utils/constants/routes";
import {
  getTenantDomain,
  getTenantId,
  TENANT_MAPPING,
} from "../utils/helpers/tenantConfig";

const AUTH_ERROR_CODES = [401, 477];
const TENANT_INACTIVE_CODE = [419];

// Add axios interceptor to include tenant headers in all requests
if (axios && axios.interceptors) {
  axios.interceptors.request.use(
    (config) => {
      const tenantDomain = getTenantDomain();
      const tenantId = getTenantId();

      if (tenantId) {
        config.headers["tenantId"] = tenantId;
      }

      if (tenantDomain) {
        config.headers["tenantDomain"] = tenantDomain;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
}

// Helper function to create headers with tenant information
const createHeaders = (additionalHeaders = {}) => {
  const tenantDomain = getTenantDomain();
  const tenantId = getTenantId();

  const headers = {
    ...additionalHeaders,
  };

  if (tenantId) {
    headers["tenantId"] = tenantId;
  }

  if (tenantDomain) {
    headers["tenantDomain"] = tenantDomain;
  }

  return headers;
};

// Helper for waiting for refresh token
const waitForRefreshAndRetry = async (callback) => {
  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      if (!window.refresh_token_calling) {
        clearInterval(interval);
        try {
          const res = await callback();
          resolve(res);
        } catch (retryError) {
          reject(retryError);
        }
      }
    }, 100);
  });
};

// Get Request
export const GET = async (url) => {
  try {
    const headers = createHeaders();
    return await axios.get(url, { headers });
  } catch (error) {
    throw error?.response;
  }
};

// Post request
export const POST = async (url, payload, navigate = () => {}) => {
  try {
    const headers = createHeaders();
    return await axios.post(url, payload, { headers });
  } catch (error) {
    if (TENANT_INACTIVE_CODE.includes(error?.response?.status)) {
      navigate(INVALID_TENANT_ROUTE_PATH);
    }
    // Bug 2 Fix: Only redirect if NOT calling the refresh-token endpoint itself.
    if (error?.response?.status === 401 && url !== authEndpoints.checkLogin) {
      navigate(LOGIN_PATH);
    }
    throw error?.response;
  }
};

// Protected Api's Wrapper (Get)
export const getRequest = async (navigate, url) => {
  try {
    const headers = createHeaders();
    return await axios.get(url, { headers });
  } catch (error) {
    if (AUTH_ERROR_CODES.includes(error?.response?.status)) {
      try {
        if (!window.refresh_token_calling) {
          window.refresh_token_calling = true;
          await axios.post(authEndpoints.checkLogin);
          window.refresh_token_calling = false;
          const headers = createHeaders();
          return await axios.get(url, { headers });
        } else {
          return waitForRefreshAndRetry(() => {
            const headers = createHeaders();
            return axios.get(url, { headers });
          });
        }
      } catch (err) {
        window.refresh_token_calling = false;
        if (AUTH_ERROR_CODES.includes(err?.response?.status)) {
          EventEmitter.emit(
            EMITTER_ERROR,
            err?.response?.data?.message || err?.message,
          );
          navigate(LOGIN_PATH);
        }
        throw err?.response;
      }
    } else if (
      error?.response?.status === 426 ||
      error?.response?.status === 427
    ) {
      navigate(INITIAL_ROUTE);
    } else if (TENANT_INACTIVE_CODE.includes(error?.response?.status)) {
      navigate(INVALID_TENANT_ROUTE_PATH);
    } else {
      throw error?.response;
    }
  }
};

// Protected Api's Wrapper (Post)
export const postRequest = async (navigate, url, payload, headers = {}) => {
  try {
    const finalHeaders = createHeaders(headers);
    return await axios.post(url, payload, { headers: finalHeaders });
  } catch (error) {
    if (AUTH_ERROR_CODES.includes(error?.response?.status)) {
      try {
        if (!window.refresh_token_calling) {
          window.refresh_token_calling = true;
          await axios.post(authEndpoints.checkLogin);
          window.refresh_token_calling = false;
          const finalHeaders = createHeaders(headers);
          return await axios.post(url, payload, { headers: finalHeaders });
        } else {
          return waitForRefreshAndRetry(() => {
            const finalHeaders = createHeaders(headers);
            return axios.post(url, payload, { headers: finalHeaders });
          });
        }
      } catch (err) {
        window.refresh_token_calling = false;
        if (AUTH_ERROR_CODES.includes(err?.response?.status)) {
          EventEmitter.emit(
            EMITTER_ERROR,
            err?.response?.data?.message || err?.message,
          );
          navigate(LOGIN_PATH);
        }
        throw err?.response;
      }
    } else if (error?.response?.status === 426) {
      navigate(INITIAL_ROUTE);
    } else if (TENANT_INACTIVE_CODE.includes(error?.response?.status)) {
      navigate(INVALID_TENANT_ROUTE_PATH);
    } else {
      throw error?.response;
    }
  }
};

// Protected Api's Wrapper (Post Stream)
export const postStreamRequest = async (
  navigate,
  url,
  payload,
  headers = {},
) => {
  try {
    // Manually construct the full URL using axios's baseURL
    const baseURL = axios.defaults.baseURL || "";

    // Ensure proper URL construction with exactly one slash
    let fullURL;
    if (url.startsWith("http")) {
      fullURL = url;
    } else {
      // Remove trailing slash from baseURL and leading slash from url, then join with single slash
      const cleanBaseURL = baseURL.endsWith("/")
        ? baseURL.slice(0, -1)
        : baseURL;
      const cleanUrl = url.startsWith("/") ? url.slice(1) : url;
      fullURL = `${cleanBaseURL}/${cleanUrl}`;
    }

    const finalHeaders = createHeaders({
      ...headers,
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    });

    const response = await fetch(fullURL, {
      method: "POST",
      headers: finalHeaders,
      body: JSON.stringify(payload),
      credentials: "include", // Important: include cookies
    });

    if (!response.ok) {
      if (AUTH_ERROR_CODES.includes(response.status)) {
        try {
          if (!window.refresh_token_calling) {
            window.refresh_token_calling = true;
            await axios.post(authEndpoints.checkLogin);
            window.refresh_token_calling = false;

            const retryHeaders = createHeaders({
              ...headers,
              "Content-Type": "application/json",
              Accept: "text/event-stream",
            });

            return await fetch(fullURL, {
              method: "POST",
              headers: retryHeaders,
              body: JSON.stringify(payload),
              credentials: "include",
            });
          } else {
            return waitForRefreshAndRetry(() => {
              const retryHeaders = createHeaders({
                ...headers,
                "Content-Type": "application/json",
                Accept: "text/event-stream",
              });

              return fetch(fullURL, {
                method: "POST",
                headers: retryHeaders,
                body: JSON.stringify(payload),
                credentials: "include",
              });
            });
          }
        } catch (err) {
          window.refresh_token_calling = false;
          if (
            err instanceof Response &&
            AUTH_ERROR_CODES.includes(err.status)
          ) {
            const errorData = await err.json().catch(() => ({}));
            EventEmitter.emit(
              EMITTER_ERROR,
              errorData?.message || "Authentication failed",
            );
            navigate(LOGIN_PATH);
          }
          throw err;
        }
      } else if (response.status === 426) {
        navigate(INITIAL_ROUTE);
      } else if (TENANT_INACTIVE_CODE.includes(response.status)) {
        navigate(INVALID_TENANT_ROUTE_PATH);
      } else {
        throw response;
      }
    }

    return response;
  } catch (error) {
    if (error instanceof Response) {
      throw error;
    }
    console.error("Stream request failed:", error);
    throw error;
  }
};

// Protected Api's Wrapper (Put)
export const putRequest = async (navigate, url, payload) => {
  try {
    const headers = createHeaders();
    return await axios.put(url, payload, { headers });
  } catch (error) {
    if (AUTH_ERROR_CODES.includes(error?.response?.status)) {
      try {
        if (!window.refresh_token_calling) {
          window.refresh_token_calling = true;
          await axios.post(authEndpoints.checkLogin);
          window.refresh_token_calling = false;
          const headers = createHeaders();
          return await axios.put(url, payload, { headers });
        } else {
          return waitForRefreshAndRetry(() => {
            const headers = createHeaders();
            return axios.put(url, payload, { headers });
          });
        }
      } catch (err) {
        window.refresh_token_calling = false;
        if (AUTH_ERROR_CODES.includes(err?.response?.status)) {
          EventEmitter.emit(
            EMITTER_ERROR,
            err?.response?.data?.message || err?.message,
          );
          navigate(LOGIN_PATH);
        } else if (TENANT_INACTIVE_CODE.includes(error?.response?.status)) {
          navigate(INVALID_TENANT_ROUTE_PATH);
        }
        throw error?.response;
      }
    } else {
      throw error?.response;
    }
  }
};

// Protected Api's Wrapper (Patch)
export const patchRequest = async (navigate, url, payload) => {
  try {
    const headers = createHeaders();
    return await axios.patch(url, payload, { headers });
  } catch (error) {
    if (AUTH_ERROR_CODES.includes(error?.response?.status)) {
      try {
        if (!window.refresh_token_calling) {
          window.refresh_token_calling = true;
          await axios.post(authEndpoints.checkLogin);
          window.refresh_token_calling = false;
          const headers = createHeaders();
          return await axios.patch(url, payload, { headers });
        } else {
          return waitForRefreshAndRetry(() => {
            const headers = createHeaders();
            return axios.patch(url, payload, { headers });
          });
        }
      } catch (err) {
        window.refresh_token_calling = false;
        if (AUTH_ERROR_CODES.includes(err?.response?.status)) {
          EventEmitter.emit(
            EMITTER_ERROR,
            err?.response?.data?.message || err?.message,
          );
          navigate(LOGIN_PATH);
        } else if (TENANT_INACTIVE_CODE.includes(error?.response?.status)) {
          navigate(INVALID_TENANT_ROUTE_PATH);
        }
        throw error?.response;
      }
    } else {
      throw error?.response;
    }
  }
};

// Protected Api's Wrapper (Delete)
export const deleteRequest = async (url, payload, navigate) => {
  try {
    const headers = createHeaders();
    return await axios.delete(url, { data: payload, headers });
  } catch (error) {
    if (AUTH_ERROR_CODES.includes(error?.response?.status)) {
      try {
        if (!window.refresh_token_calling) {
          window.refresh_token_calling = true;
          await axios.post(authEndpoints.checkLogin);
          window.refresh_token_calling = false;
          const headers = createHeaders();
          return await axios.delete(url, { data: payload, headers });
        } else {
          return waitForRefreshAndRetry(() => {
            const headers = createHeaders();
            return axios.delete(url, { data: payload, headers });
          });
        }
      } catch (err) {
        window.refresh_token_calling = false;
        if (AUTH_ERROR_CODES.includes(err?.response?.status)) {
          EventEmitter.emit(
            EMITTER_ERROR,
            err?.response?.data?.message || err?.message,
          );
          navigate(LOGIN_PATH);
        }
        throw error?.response;
      }
    } else if (TENANT_INACTIVE_CODE.includes(error?.response?.status)) {
      navigate(INVALID_TENANT_ROUTE_PATH);
    } else {
      throw error?.response;
    }
  }
};
